import _ from 'lodash';
import produce from 'immer';
import { Prompt, useHistory } from 'react-router-dom';
import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { useDispatch } from 'react-redux';

import { Archive } from 'src/presentation/components/pages/shared/rowActions/Archive';
import { Button } from 'src/presentation/components/agent_desktop_components';
import customerProfileDefResource from 'src/actions/customer/customerProfileDefResource';
import { Edit } from '../shared/rowActions/Edit';
import RouterLinkButton from 'src/presentation/components/RouterLinkButton';
import routingGroupsResource from 'src/actions/routingGroups/routingGroupsResource';
import { RoutingPriority } from 'src/actions/rules/actions';
import ruleSetsResource, { RuleSetType } from 'src/actions/rules/ruleSetsResource';
import rulesResource, { Rule, RuleStatus } from 'src/actions/rules/rulesResource';
import SmartMatchRuleEditor from './SmartMatchRuleEditor';
import { StyledConfirmOrCancelModal } from '../shared/rowActions/StyledConfirmOrCancelModal';
import TablePage, { TablePageRender } from '../shared/TablePage';
import { toDateTimeString } from 'src/presentation/components/pages/shared/utils/TimeUtils';
import useDocumentResource from 'src/presentation/hooks/useDocumentResource';
import useResource, { isLoadingStatus } from 'src/presentation/hooks/useResource';
import useSkills from 'src/presentation/hooks/useSkills';
import useTopics from 'src/presentation/hooks/useTopics';
import useAppPlatformDataAttributes from './hooks/useAppPlatformDataAttributes';
import useIsFeatureEnabled from 'src/presentation/hooks/useIsFeatureEnabled';

const dataAidPrefix = 'admin-smart-match';
const columnConfig = [
  {
    attr: 'name',
    label: 'Name',
    customColumnStyle: () => ({ width: '400px' }),
  },
  {
    attr: 'priority',
    label: 'Boost Size',
    customRenderer: (item: Rule) => <RulePriority item={item} />,
    customColumnStyle: () => ({ width: '150px' }),
  },
  {
    attr: 'updatedAt',
    label: 'Last Modified',
    customRenderer: (item: Rule) => {
      if (!item.updatedAt) {
        return '';
      }
      return toDateTimeString(item.updatedAt, ' ');
    },
  },
  {
    attr: 'status',
    label: 'Notes',
    customRenderer: (item: Rule) => (item.status === RuleStatus.Mutable ? 'Change made' : ''),
  },
];
const tabConfig = [
  { label: 'Active', filter: item => !item.disabled, key: 'tab-enabled' },
  { label: 'Archived', filter: item => item.disabled, key: 'tab-disabled' },
];

const SmartMatchRules: TablePageRender = ({ root }) => {
  useResource(routingGroupsResource);
  useSkills();
  useTopics();
  useDocumentResource(customerProfileDefResource);

  const isFeatureEnabled = useIsFeatureEnabled();
  const allowAppPlatformDataAttributes = isFeatureEnabled('appPlatformDataAttributes');
  useAppPlatformDataAttributes({ fetch: allowAppPlatformDataAttributes });

  const { errors, loadingState, values } = useResource(rulesResource, {
    fetch: true,
    params: { ruleSetType: RuleSetType.SmartMatch },
  });
  const rules: Rule[] = _.chain(values)
    .filter(item => item.trigger.type === 'SMART_MATCH')
    .orderBy(['actions.setPriority.priority', item => item.name.toLowerCase()], ['desc', 'asc'])
    .value();

  const hasPendingRuleUpdates = _.some(rules, { status: RuleStatus.Mutable });

  function createRuleSet() {
    dispatch(
      ruleSetsResource.effects.add(
        {},
        { type: RuleSetType.SmartMatch },
        {
          customToastMsg: 'Your changes have been applied successfully',
          disableWindowBack: true,
        }
      )
    );
  }

  const actions = (
    <>
      <PrimaryButton
        data-aid={`${dataAidPrefix}-enableChanges`}
        onClick={createRuleSet}
        disabled={!hasPendingRuleUpdates}
      >
        Enable Changes
      </PrimaryButton>
      <RouterLinkButton data-aid={`${dataAidPrefix}-addButton`} to={`${root}/new`}>
        Add Priority Boost
      </RouterLinkButton>
    </>
  );

  const description = (
    <span>
      Prioritizing matches between customers and agents. To learn more about People Match, visit{' '}
      <a
        data-aid={`${dataAidPrefix}-helpLink`}
        target="_blank"
        rel="noopener noreferrer"
        href="https://help.gladly.com/docs/what-is-people-match/"
      >
        Help Docs
      </a>
      .
    </span>
  );

  const dispatch = useDispatch();
  const onDisabledUpdate = (item: Rule) => {
    const toast = `We ${item.disabled ? 'un' : ''}archived ${
      item.name
    }. Click on "Enable changes" for the change to take effect.`;

    const updatedItem = produce(item, draft => {
      draft.disabled = !draft.disabled;
      draft._version++;
    });

    dispatch(
      rulesResource.effects.update({ id: item.id }, updatedItem, {
        customToastMsg: toast,
        disableWindowBack: true,
      })
    );
  };

  function renderRowActions({ item }) {
    const actionRoot = item.disabled ? 'Unarchiv' : 'Archiv';
    return (
      <>
        <Archive
          confirmationText={`${actionRoot}e ${item.name} ?`}
          description={`${actionRoot}ing this Priority Boost will change how customers are matched once you enable changes.`}
          submitText={`${actionRoot}e Priority Boost`}
          itemId={item.id}
          onArchive={() => onDisabledUpdate(item)}
          isUnarchive={item.disabled}
        />
        <Edit id={item.id} />
      </>
    );
  }

  function filterFunc(items: Rule[], searchString: string) {
    if (!searchString) {
      return items;
    }
    return _.filter(items, item => item.name.toLowerCase().indexOf(searchString.toLowerCase()) > -1);
  }

  const [isModalVisible, setModalVisible] = useState(false);
  const [attemptedLocation, setAttemptedLocation] = useState<Location | null>(null);
  const [isLeaveConfirmed, setLeaveConfirmed] = useState(false);

  const history = useHistory();

  const closeModal = () => {
    setModalVisible(false);
  };

  const handleBlockedNavigation = nextLocation => {
    if (!isLeaveConfirmed && !nextLocation.pathname.includes('people-match')) {
      setModalVisible(true);
      setAttemptedLocation(nextLocation);
      return false;
    }
    return true;
  };

  const handleConfirmNavigationClick = () => {
    setModalVisible(false);
    setLeaveConfirmed(true);
  };

  useEffect(() => {
    if (isLeaveConfirmed && attemptedLocation) {
      history.push(attemptedLocation.pathname);
    }
  }, [isLeaveConfirmed, attemptedLocation, history]);

  const modalText = [
    "If you're ready for your saved changes to take effect, click 'Enable Changes'.",
    <br key={`${dataAidPrefix}-modal-br`} />,
    "If you're not ready, your changes will still be saved and you can enable them at a later time.",
  ];

  return (
    <>
      {isModalVisible ? (
        <CancelConfirmationModal
          onCancel={handleConfirmNavigationClick}
          onSubmit={closeModal}
          confirmOnDismiss
          submitText="Stay on Page"
          cancelText="Leave"
          text={modalText}
          title="Your changes are not enabled yet"
        />
      ) : (
        <Prompt when={hasPendingRuleUpdates} message={handleBlockedNavigation} />
      )}
      <TablePage
        actions={actions}
        columnConfig={columnConfig}
        data-aid={`${dataAidPrefix}-page`}
        description={description}
        editor={SmartMatchRuleEditor}
        errors={errors}
        filterFunc={filterFunc}
        isLoading={isLoadingStatus(loadingState)}
        items={rules}
        name="People Match"
        renderRowActions={renderRowActions}
        resetErrors={rulesResource.actions.resetErrors}
        root={root}
        tabConfig={tabConfig}
      />
    </>
  );
};

function RulePriority({ item }: { item: Rule }) {
  return (
    <span data-aid={`${dataAidPrefix}-cell-priority-${item.id}`} key={`${dataAidPrefix}-cell-priority-${item.id}`}>
      {translatePriority(item.actions.setPriority && item.actions.setPriority.priority)}
    </span>
  );
}

function translatePriority(priority) {
  switch (priority) {
    case RoutingPriority.Extreme:
      return 'Very Large';
    case RoutingPriority.High:
      return 'Large';
    case RoutingPriority.Medium:
      return 'Medium';
    case RoutingPriority.Low:
    default:
      return 'Small';
  }
}

const PrimaryButton = styled(Button)`
  margin-right: 16px;
`;

const CancelConfirmationModal = styled(StyledConfirmOrCancelModal)`
  position: fixed;
  width: 100vh;
  height: 100vh;
  z-index: 100;
`;

export default SmartMatchRules;
